import React from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, TextField, Switch, FormControlLabel } from "@mui/material";
import { ImageIconGrid } from 'qwanyx';

export const ImageGrid = ({
  name = "",
  icons = [],
  style = {},
  ratio = "1/1",
  imageWidth = "64px",
  columnsPerRow = 3,
  imageClassName = "",
  textStyle = {},
  textClassName = "",
  displayIcons = null,
  random = false,
  interval = null,
  animateClass = "",
  hidden = false,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  if (hidden) return null;

  // Selection styling - only show when editor is enabled
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        width: "100%",
        minHeight: "100px",
        ...getSelectionStyle()
      }}
      {...props}
    >
      <ImageIconGrid
        icons={icons}
        style={style}
        ratio={ratio}
        imageWidth={imageWidth}
        columnsPerRow={columnsPerRow}
        imageClassName={imageClassName}
        textStyle={textStyle}
        textClassName={textClassName}
        displayIcons={displayIcons}
        random={random}
        interval={interval}
        animateClass={animateClass}
      />
    </div>
  );
};

const ImageGridSettings = () => {
  const { 
    actions: { setProp }, 
    name,
    icons,
    style,
    ratio,
    imageWidth,
    columnsPerRow,
    imageClassName,
    textStyle,
    textClassName,
    displayIcons,
    random,
    interval,
    animateClass
  } = useNode((node) => ({
    name: node.data.props.name,
    icons: node.data.props.icons || [],
    style: node.data.props.style || {},
    ratio: node.data.props.ratio || "1/1",
    imageWidth: node.data.props.imageWidth || "64px",
    columnsPerRow: node.data.props.columnsPerRow || 3,
    imageClassName: node.data.props.imageClassName || "",
    textStyle: node.data.props.textStyle || {},
    textClassName: node.data.props.textClassName || "",
    displayIcons: node.data.props.displayIcons,
    random: node.data.props.random || false,
    interval: node.data.props.interval,
    animateClass: node.data.props.animateClass || ""
  }));

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Icons JSON</FormLabel>
        <TextField
          multiline
          rows={8}
          value={JSON.stringify(icons, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setProp(props => props.icons = parsed);
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          size="small"
          placeholder={`[
  {
    "url": "https://example.com/image.jpg",
    "text": "Icon Label",
    "target": {
      "_id": "card123",
      "di": "domain456",
      "ed": "parent789",
      "pa": "editor012"
    }
  }
]`}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Container Style JSON</FormLabel>
        <TextField
          multiline
          rows={3}
          value={JSON.stringify(style, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setProp(props => props.style = parsed);
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          size="small"
          placeholder='{"padding": "20px", "backgroundColor": "#f5f5f5"}'
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Style JSON</FormLabel>
        <TextField
          multiline
          rows={3}
          value={JSON.stringify(textStyle, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setProp(props => props.textStyle = parsed);
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          size="small"
          placeholder='{"fontSize": "14px", "color": "#333"}'
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Ratio</FormLabel>
        <TextField
          value={ratio}
          onChange={(e) => setProp(props => props.ratio = e.target.value)}
          size="small"
          placeholder="1/1, 16/9, 4/3, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Image Width</FormLabel>
        <TextField
          value={imageWidth}
          onChange={(e) => setProp(props => props.imageWidth = e.target.value)}
          size="small"
          placeholder="64px, 100px, 5rem, etc."
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Columns Per Row</FormLabel>
        <TextField
          type="number"
          value={columnsPerRow}
          onChange={(e) => setProp(props => props.columnsPerRow = parseInt(e.target.value) || 3)}
          size="small"
          inputProps={{ min: 1, max: 12 }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Image Class Name</FormLabel>
        <TextField
          value={imageClassName}
          onChange={(e) => setProp(props => props.imageClassName = e.target.value)}
          size="small"
          placeholder="CSS class names for images"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Text Class Name</FormLabel>
        <TextField
          value={textClassName}
          onChange={(e) => setProp(props => props.textClassName = e.target.value)}
          size="small"
          placeholder="CSS class names for text"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Display Icons (null = all)</FormLabel>
        <TextField
          type="number"
          value={displayIcons || ''}
          onChange={(e) => setProp(props => props.displayIcons = e.target.value ? parseInt(e.target.value) : null)}
          size="small"
          placeholder="Number of icons to show"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Animation Interval (ms)</FormLabel>
        <TextField
          type="number"
          value={interval || ''}
          onChange={(e) => setProp(props => props.interval = e.target.value ? parseInt(e.target.value) : null)}
          size="small"
          placeholder="Milliseconds between animations"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Animate.css Class</FormLabel>
        <TextField
          value={animateClass}
          onChange={(e) => setProp(props => props.animateClass = e.target.value)}
          size="small"
          placeholder="bounce, fadeIn, slideInUp, etc."
        />
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={random}
            onChange={(e) => setProp(props => props.random = e.target.checked)}
          />
        }
        label="Random Selection"
      />
    </div>
  );
};

ImageGrid.craft = {
  props: {
    name: "",
    icons: [],
    style: {},
    ratio: "1/1",
    imageWidth: "64px",
    columnsPerRow: 3,
    imageClassName: "",
    textStyle: {},
    textClassName: "",
    displayIcons: null,
    random: false,
    interval: null,
    animateClass: ""
  },
  related: {
    settings: ImageGridSettings
  }
};

export default ImageGrid;